/**
 * What a model is allowed to say back.
 *
 * A mind map is a tree of short labels, and that is exactly what this schema describes: a title
 * and nested nodes with text and children. Nothing else. No positions, no colours, no ids — those
 * are the product's to decide, and a model that could name an id could name one that already
 * exists.
 *
 * **The caps are part of the contract, not a safety net.** A model asked for "a map about the
 * Roman Empire" will happily return four hundred nodes eleven levels deep, and the result is not a
 * mind map — it is an essay with bullet points, unreadable on a screen and unusable as a starting
 * point. Refusing at the boundary is also what stops a runaway generation from becoming a
 * runaway *document*: every one of these numbers is a bound on what a single request can cost in
 * layout, in storage and in a person's attention.
 *
 * **A refusal here is `AI_OUTPUT_INVALID`, and it is expected rather than exceptional.** Models
 * return prose instead of JSON, JSON with a trailing comma, and objects with the right shape and
 * an empty label. `parseAt` reports which, the caller offers to try again, and nothing reaches a
 * document until it has been through here.
 */
import * as z from 'zod';
/** How deep a generated map may go. Three levels below the root is a map somebody can read. */
export const MAX_OUTLINE_DEPTH = 4;
/** How many nodes in total. Past this it is an essay with bullet points. */
export const MAX_OUTLINE_NODES = 60;
/** How long one label may be. A node is a label, not a paragraph — §3's rule about node text. */
export const MAX_LABEL_LENGTH = 120;
/** How long a node's note may be: a sentence or two that explains it, not an essay. */
export const MAX_NOTE_LENGTH = 280;
/**
 * The arrangements a model may suggest, in the words a person would use for them.
 *
 * Words rather than layout ids, because an id is the product's vocabulary and a model that could
 * name one could name one that does not exist. The engine that builds the document decides which
 * layout each word means.
 */
export const LAYOUT_SUGGESTIONS = [
    'mind map',
    'radial',
    'org chart',
    'fishbone',
    'outline',
    'columns',
];
const label = z.string().trim().min(1).max(MAX_LABEL_LENGTH);
/*
 * The optional parts of an answer are read leniently, where the label is read strictly.
 *
 * A label is the map; a note that runs long, an "emoji" that is a word, a layout the list does not
 * have — those are a model decorating, and throwing away forty good labels because one decoration
 * was wrong would make the options a way to break a draft. So each is cut to size, or dropped.
 */
/** A note, cut to size; an empty one is none. */
const note = z.string().transform((value) => {
    const trimmed = value.trim().slice(0, MAX_NOTE_LENGTH).trim();
    return trimmed === '' ? undefined : trimmed;
});
/** One pictograph and whatever joins or varies it; anything else is not a glyph and is dropped. */
const glyph = z.string().transform((value) => {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed.length > 16)
        return undefined;
    return /^\p{Extended_Pictographic}/u.test(trimmed) ? trimmed : undefined;
});
/** A suggestion from the list, whatever case it came in; anything else is no suggestion. */
const suggestion = z.string().transform((value) => {
    const wanted = value.trim().toLowerCase().replace(/[-_]/g, ' ');
    return LAYOUT_SUGGESTIONS.find((known) => known === wanted);
});
/**
 * Recursive, and bounded by construction.
 *
 * `z.lazy` is what lets a node contain nodes; the depth cap cannot be expressed inside it, so it
 * is checked afterwards by `outlineIssues` — which is also where the total-node cap lives. Two
 * checks rather than one enormous schema, because the messages a person sees should say "too
 * deep" and "too many", not "invalid at nodes.3.children.2.children".
 */
export const outlineNodeSchema = z.lazy(() => z
    .object({
    text: label,
    // Absent and empty mean the same thing, and a model will produce both.
    children: z.array(outlineNodeSchema).default([]),
    note: note.optional(),
    emoji: glyph.optional(),
})
    .strict());
export const outlineSchema = z
    .object({
    title: label,
    nodes: z.array(outlineNodeSchema).min(1),
    /** The arrangement the model thinks suits the subject, when it was asked to choose one. */
    layout: suggestion.optional(),
})
    .strict();
/** How many nodes an outline holds, root excluded. */
export function countOutlineNodes(nodes) {
    return nodes.reduce((total, node) => total + 1 + countOutlineNodes(node.children), 0);
}
/** How far below the root it goes. A flat list of children is depth 1. */
export function outlineDepth(nodes) {
    return nodes.reduce((deepest, node) => Math.max(deepest, 1 + outlineDepth(node.children)), 0);
}
/**
 * What is wrong with an outline that parsed.
 *
 * Separate from the schema because the two answer different questions: the schema asks "is this
 * the shape of an outline", and this asks "is it a mind map anybody can use". A model that
 * returns something enormous has not malfunctioned — it has been too enthusiastic — and the
 * difference matters to what the interface offers to do next.
 */
export function outlineIssues(outline, limits = OUTLINE_LIMITS) {
    const issues = [];
    const depth = outlineDepth(outline.nodes);
    if (depth > limits.depth) {
        issues.push({
            code: 'too-deep',
            detail: `${String(depth)} levels, and ${String(limits.depth)} is the most this map may have`,
        });
    }
    const nodes = countOutlineNodes(outline.nodes);
    if (nodes > limits.nodes) {
        issues.push({
            code: 'too-many-nodes',
            detail: `${String(nodes)} nodes, and ${String(limits.nodes)} is the most this map may have`,
        });
    }
    return issues;
}
export const OUTLINE_LIMITS = { depth: MAX_OUTLINE_DEPTH, nodes: MAX_OUTLINE_NODES };
/**
 * Cuts an outline down to what the caps allow, breadth-first.
 *
 * Breadth-first on purpose: the first level is the shape of the idea and the deepest level is
 * detail. Trimming from the bottom leaves a map that is still *about* the thing somebody asked
 * for; trimming from the end of the list would leave one that stops mid-thought.
 *
 * Offered rather than applied automatically — `outlineIssues` is what a caller checks first, and
 * whether to trim or to ask the model again is a decision for the screen that has a person in
 * front of it.
 */
export function trimOutline(outline, limits = OUTLINE_LIMITS) {
    let budget = Math.min(limits.nodes, MAX_OUTLINE_NODES);
    const deepest = Math.min(limits.depth, MAX_OUTLINE_DEPTH);
    const trim = (nodes, depth) => {
        if (depth >= deepest)
            return [];
        const kept = [];
        for (const node of nodes) {
            if (budget <= 0)
                break;
            budget -= 1;
            // Everything the node says about itself stays; only what hangs below it is decided here.
            kept.push({ ...node, children: [] });
        }
        // The children of everything kept at this level, before anything deeper — which is what makes
        // it breadth-first rather than a depth-first walk that spends the whole budget on one branch.
        return kept.map((node, index) => ({
            ...node,
            children: trim(nodes[index]?.children ?? [], depth + 1),
        }));
    };
    return { ...outline, nodes: trim(outline.nodes, 0) };
}
//# sourceMappingURL=ai.js.map